// ../components/advisor/AdvisorSubscriptionContainer.jsx

import React, { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { supabase, getFunctionsUrl, getAccessToken } from "../../lib/supabaseClient";
import { Shield, Users, Zap, Check, RefreshCw, Info } from "lucide-react";

export default function AdvisorSubscriptionContainer() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState(null);
  const [clientsCount, setClientsCount] = useState(0);
  const [error, setError] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  
  // Protection critique contre la boucle infinie
  const hasLoadedRef = useRef(false);
  const loadingRef = useRef(false);
  const userIdRef = useRef(null);

  useEffect(() => {
    // PROTECTION 1 : Éviter les appels multiples simultanés
    if (loadingRef.current) {
      console.log("⏸️ Chargement déjà en cours, skip");
      return;
    }

    // PROTECTION 2 : Ne charger qu'une seule fois par utilisateur
    if (hasLoadedRef.current && userIdRef.current === user?.id) {
      console.log("✅ Données déjà chargées pour cet utilisateur");
      return;
    }

    // PROTECTION 3 : Vérifier qu'on a un utilisateur
    if (!user?.id) {
      setLoading(false);
      return;
    }

    // Marquer le début du chargement
    loadingRef.current = true;
    userIdRef.current = user.id;

    const loadData = async () => {
      setError(null);
      setLoading(true);
      
      try {
        // Charger les entitlements
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Session expirée");
        }

        const functionUrl = getFunctionsUrl("advisor-subscription");
        console.log("📡 Chargement abonnement...");
       
        // Timeout augmenté à 15 secondes pour les Edge Functions lentes
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.log("⏱️ Timeout atteint (15s), abandon...");
          controller.abort();
        }, 15000);

        const resp = await fetch(functionUrl, {
          method: "GET",
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        }).catch(err => {
          clearTimeout(timeoutId);
          // Si c'est un AbortError, on passe directement au fallback
          if (err.name === 'AbortError') {
            console.log("⏱️ Timeout - passage en mode fallback silencieux");
            const timeoutError = new Error("Edge Function timeout");
            timeoutError.name = 'TimeoutError';
            timeoutError.isSilent = true; // Ne pas afficher d'erreur visible
            throw timeoutError;
          }
          throw err;
        });
        
        clearTimeout(timeoutId);
        
        let data;
        const contentType = resp.headers.get("content-type");
        
        if (contentType?.includes("application/json")) {
          data = await resp.json();
        } else {
          const text = await resp.text();
          throw new Error(`Réponse invalide: ${text.substring(0, 100)}`);
        }

        if (!resp.ok) {
          throw new Error(data?.error || `Erreur HTTP ${resp.status}`);
        }

        setEntitlements(data);
        console.log("✅ Abonnement chargé");
        
      } catch (e) {
        console.error("❌ Erreur abonnement:", e.name, e.message);
        
        // Ne pas afficher d'erreur pour les timeouts silencieux
        if (!e.isSilent) {
          setError({
            message: e.message,
            isNetworkError:
              e.name === "AbortError" ||
              e.name === "TimeoutError" ||
              e.message.includes("fetch") ||
              e.message.includes("Failed to fetch")
          });
        }

        // Fallback : charger depuis la BDD
        try {
          console.log("🔄 Chargement fallback depuis BDD...");
          
          const { data: subData, error: subError } = await supabase
            .from("advisor_subscriptions")
            .select("*")
            .eq("advisor_id", user.id)
            .maybeSingle();

          if (subError) throw subError;

          if (subData) {
            setEntitlements({
              plan: subData.plan || "advisor",
              status: subData.status || "active",
              max_clients: (subData.clients_limit || 10) + (subData.addons_clients || 0),
              addons_clients: subData.addons_clients || 0,
              has_vision_access: subData.plan === "advisor_premium",
              billing_method: subData.billing_method || "manual",
              _isFallback: true
            });
            console.log("✅ Chargé en mode fallback");
          } else {
            // Créer un abonnement par défaut
            const { error: insertError } = await supabase
              .from("advisor_subscriptions")
              .insert({
                advisor_id: user.id,
                plan: "advisor",
                status: "active",
                billing_method: "manual",
                clients_limit: 10,
                addons_clients: 0
              });

            if (insertError) throw insertError;

            setEntitlements({
              plan: "advisor",
              status: "active",
              max_clients: 10,
              addons_clients: 0,
              has_vision_access: false,
              billing_method: "manual",
              _isFallback: true
            });
            console.log("✅ Abonnement créé");
          }
        } catch (fallbackError) {
          console.error("❌ Échec fallback:", fallbackError);
          
          // Dernier recours
          setEntitlements({
            plan: "advisor",
            status: "active",
            max_clients: 10,
            addons_clients: 0,
            has_vision_access: false,
            billing_method: "manual",
            _isFallback: true,
            _readonly: true
          });
        }
      } finally {
        setLoading(false);
        loadingRef.current = false;
        hasLoadedRef.current = true;
      }
    };

    // Charger les clients
    const loadClients = async () => {
      try {
        const { count, error } = await supabase
          .from("v_expert_clients")
          .select("*", { count: "exact", head: true })
          .eq("expert_id", user.id);
        
        if (!error) {
          setClientsCount(count || 0);
        }
      } catch (e) {
        console.error("❌ Erreur clients:", e);
      }
    };

    // Exécuter les chargements
    Promise.all([loadData(), loadClients()]);

    // Pas de cleanup nécessaire car on utilise les refs
  }, [user?.id, refreshNonce]); // user + refreshNonce

  const managedQuota = useMemo(() => {
    const max = entitlements?.max_clients ?? 0;
    return { max, used: clientsCount, left: Math.max(0, max - clientsCount) };
  }, [entitlements?.max_clients, clientsCount]);

  const canManageBilling = entitlements?.billing_method === "stripe";
  const isPremium = !!entitlements?.has_vision_access;
  const isFallbackMode = entitlements?._isFallback;
  const isReadOnly = entitlements?._readonly;

  const handleChangePlan = async (target) => {
    if (isReadOnly) {
      alert("Modification impossible en mode lecture seule");
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessToken();
      const resp = await fetch(getFunctionsUrl("advisor-subscription"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ plan: target })
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || "Échec");

      setEntitlements(data);
    } catch (e) {
      alert(`Erreur: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    hasLoadedRef.current = false;
    userIdRef.current = null;
    setLoading(true);
    setEntitlements(null);
    setError(null); // Reset l'erreur aussi
    setRefreshNonce((n) => n + 1); // force le useEffect à rerun
  };

  if (loading && !entitlements) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8">
        <div className="flex items-center justify-center gap-3">
          <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
          <div className="text-gray-600">Chargement de l'abonnement…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-light">Abonnement Art Advisor</h2>
          <div className="flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">
              {entitlements?.billing_method === "manual" && "Facturation manuelle"}
              {entitlements?.billing_method === "sepa" && "Prélèvement SEPA"}
              {entitlements?.billing_method === "stripe" && "Paiement Stripe"}
            </span>
          </div>
        </div>

        {/* Alertes */}
        {isFallbackMode && (
          <div className="mb-4 flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 px-4 py-2.5 rounded-lg">
            <Info className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-medium">Mode dégradé</span>
              {isReadOnly && " • Lecture seule"}
            </div>
            {error && (
              <button 
                onClick={() => setShowDebug(!showDebug)}
                className="text-xs underline"
              >
                {showDebug ? "Masquer" : "Détails"}
              </button>
            )}
          </div>
        )}

        {error && showDebug && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs">
            <div className="font-medium text-red-800 mb-1">Erreur détaillée</div>
            <div className="text-red-600">{error.message}</div>
            {error.isNetworkError && (
              <div className="mt-2 text-red-700">
                💡 L'Edge Function est probablement inaccessible ou trop lente. Le système fonctionne en mode dégradé.
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="p-4 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Plan</p>
            <p className="text-lg font-light">
              {isPremium ? "Advisor Premium (IA)" : "Advisor (Gestion)"}
            </p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Clients gérés</p>
            <p className="text-lg flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              {managedQuota.used} / {managedQuota.max}
            </p>
            {managedQuota.left === 0 && (
              <p className="text-xs text-red-600 mt-1">Capacité atteinte</p>
            )}
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Accès IA Vision</p>
            <p className="text-lg flex items-center gap-2">
              {isPremium ? (
                <>
                  <Zap className="w-4 h-4 text-green-600" />
                  <span>Activé</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 text-gray-300" />
                  <span className="text-gray-400">Désactivé</span>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={`border rounded-xl p-6 transition-all ${!isPremium ? "border-black shadow-sm" : "border-gray-200"}`}>
            <h3 className="text-xl font-light mb-3">Advisor (Gestion)</h3>
            <ul className="space-y-2 text-sm text-gray-600 mb-4">
              <li className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-gray-400" /> Multi-clients + rapports PDF
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-gray-400" /> Suivi acquisitions/assurances
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-gray-400" /> Export CSV/XLSX/Docx
              </li>
            </ul>
            <button
              disabled={isReadOnly || !canManageBilling || !isPremium}
              onClick={() => handleChangePlan("advisor")}
              className={`w-full py-2.5 rounded-lg text-sm font-medium transition ${
                (isReadOnly || !canManageBilling || !isPremium)
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "border border-black hover:bg-black hover:text-white"
              }`}
            >
              Advisor
            </button>
          </div>

          <div className={`border rounded-xl p-6 transition-all ${isPremium ? "border-black shadow-sm" : "border-gray-200"}`}>
            <h3 className="text-xl font-light mb-3">Advisor Premium (IA)</h3>
            <ul className="space-y-2 text-sm text-gray-600 mb-4">
              <li className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-gray-400" /> Vision.artkiv (analyse visuelle)
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-gray-400" /> Matching ventes & alertes
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-gray-400" /> Certificats blockchain
              </li>
            </ul>
            <button
              disabled={isReadOnly || !canManageBilling || isPremium}
              onClick={() => handleChangePlan("advisor_premium")}
              className={`w-full py-2.5 rounded-lg text-sm font-medium transition ${
                (isReadOnly || !canManageBilling || isPremium)
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-black text-white hover:opacity-90"
              }`}
            >
              Premium (IA)
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
          <button 
            onClick={handleRefresh}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Recharger
          </button>
        </div>
      </div>
    </div>
  );
}